import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {SectionCard} from '@/components/section-card'
import {HelpIcon} from '@/components/help-icon'
import {EmployeeSingleSelect} from '@/components/employee-single-select'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useJefeInmediato, useSetJefeDirecto} from '@/hooks/empresa/organizacion/use-jerarquia'
import {translateApiError} from '@/lib/errors'

const JEFE_FUENTE_KEY: Record<string, string> = {
  jefe_directo: 'jerarquia.fuente.jefeDirecto',
  responsable_area: 'jerarquia.fuente.responsableArea',
  owner_fallback: 'jerarquia.fuente.ownerFallback',
}

/** El jefe directo es una excepción; el responsable del área se fija en el perfil de empresa. */
export function EmployeeHierarchySection({
  businessId,
  employee,
  canEdit,
}: {
  businessId: string
  employee: {id: string; jefe_directo_employee_id: string | null}
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const {data: jefe} = useJefeInmediato(businessId, employee.id)
  const {data: roster} = useAllEmployees(businessId)
  const setJefeDirecto = useSetJefeDirecto(businessId, employee.id)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const jefeDirecto = roster?.find((e) => e.id === employee.jefe_directo_employee_id) ?? null

  return (
    <SectionCard title={t('jerarquia.sectionTitle')} testId="employee-hierarchy" bodyClassName="space-y-4 p-5">
      <div className="space-y-1">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('jerarquia.jefeInmediato')}
        </p>
        {jefe ? (
          <p className="text-sm text-foreground" data-testid="jefe-inmediato">
            {jefe.nombre ?? t('jerarquia.sinJefe')}
            <span className="ml-2 text-xs text-muted-foreground">{t(JEFE_FUENTE_KEY[jefe.fuente])}</span>
            {jefe.ambiguo && <Tag tone="amber">{t('jerarquia.ambiguo')}</Tag>}
          </p>
        ) : (
          <Skeleton className="h-5 w-40" />
        )}
      </div>

      <div className="surface-divider space-y-2 border-t pt-3">
        <p className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('jerarquia.jefeDirecto')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('jerarquia.jefeDirecto')})}
            testId="jefe-directo-tip">
            {t('jerarquia.jefeDirectoHint')}
          </HelpIcon>
        </p>
        {!editing ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground" data-testid="jefe-directo-current">
              {jefeDirecto ? `${jefeDirecto.name} ${jefeDirecto.last_name}` : t('jerarquia.sinJefeDirecto')}
            </span>
            {canEdit && (
              <button
                type="button"
                data-testid="jefe-directo-edit"
                onClick={() => {
                  setError(null)
                  setEditing(true)
                }}
                className="text-xs font-medium text-primary hover:underline">
                {jefeDirecto ? t('jerarquia.change') : t('jerarquia.assign')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <EmployeeSingleSelect
              roster={roster ?? []}
              value={jefeDirecto}
              exclude={(person) => person.id === employee.id}
              testId="jefe-directo-picker"
              onChange={(person) => {
                setError(null)
                setJefeDirecto.mutate(person?.id ?? null, {
                  onSuccess: () => setEditing(false),
                  onError: (err) => setError(translateApiError(err)),
                })
              }}
            />
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs font-medium text-muted-foreground hover:underline">
              {t('common.cancel')}
            </button>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </SectionCard>
  )
}
